/**
 * Central SEO configuration for the PUBLIC marketing site (bio.airmax.co.in).
 * One source of truth for the canonical origin, brand identity, default social
 * card, and the site-wide structured data (Organization / WebSite).
 *
 * The authenticated ERP (everything behind RequireAuth) is deliberately kept OUT
 * of search — see `public/robots.txt` and the `<Seo noindex>` in the app shell.
 */

/** Canonical, absolute origin. No trailing slash. Used for canonical + OG URLs + sitemap. */
export const SITE_URL = 'https://bio.airmax.co.in';

/** Full legal brand — used as og:site_name and the title suffix. */
export const SITE_NAME = 'Green Bay Biotech International';
/** Short brand — used for the PWA short_name and compact contexts. */
export const SITE_SHORT = 'Green Bay Bio';

/** Default meta description (~155 chars) used when a page supplies none. */
export const DEFAULT_DESCRIPTION =
  'Bio-fertilizers, bio-stimulants and crop-care that work with nature — restoring soil biology and lifting yield, quality and stress-resilience for Indian farmers.';

/** Default social-share image (og:image / twitter:image). 1200×630 recommended;
 *  we fall back to the brand logo (512×304) until a dedicated card is added. */
export const DEFAULT_OG_IMAGE = '/logo.png';

/** Organisation contact facts (mirrors site/shared.tsx CONTACT). */
export const ORG = {
  name: SITE_NAME,
  legalName: SITE_NAME,
  email: 'support@greenbaybio.com',
  telephone: '+91-33-4064-6022',
  logo: '/logo.png',
  address: {
    streetAddress: '2B, Grant Lane, 3rd Floor, Room No. 304',
    addressLocality: 'Kolkata',
    addressRegion: 'West Bengal',
    postalCode: '700012',
    addressCountry: 'IN',
  },
};

/** Resolve a path (or already-absolute URL) to an absolute URL on the canonical origin. */
export function absUrl(pathOrUrl?: string): string {
  if (!pathOrUrl) return `${SITE_URL}/`;
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  return SITE_URL + (pathOrUrl.startsWith('/') ? pathOrUrl : `/${pathOrUrl}`);
}

/** Site-wide Organization node (also emitted statically in index.html). */
export const organizationJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: ORG.name,
  legalName: ORG.legalName,
  url: `${SITE_URL}/`,
  logo: absUrl(ORG.logo),
  image: absUrl(DEFAULT_OG_IMAGE),
  description: DEFAULT_DESCRIPTION,
  email: ORG.email,
  telephone: ORG.telephone,
  address: {
    '@type': 'PostalAddress',
    streetAddress: ORG.address.streetAddress,
    addressLocality: ORG.address.addressLocality,
    addressRegion: ORG.address.addressRegion,
    postalCode: ORG.address.postalCode,
    addressCountry: ORG.address.addressCountry,
  },
};

/** WebSite node with a sitelinks-searchbox action pointing at the public catalogue. */
export const websiteJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: ORG.name,
  url: `${SITE_URL}/`,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/catalog?q={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};
